import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);

  const nextRequestId = useCallback((existing) => {
    const year = new Date().getFullYear();
    const nums = existing.filter(r => r.id && r.id.startsWith('PR-' + year + '-')).map(r => parseInt(r.id.split('-')[2]) || 0);
    return 'PR-' + year + '-' + String((nums.length ? Math.max(...nums) + 1 : 1)).padStart(4, '0');
  }, []);
  const nextBatchId = useCallback((existing) => {
    const year = new Date().getFullYear();
    const b = existing.filter(r => r.batch_id && r.batch_id.startsWith('PAY-BATCH-' + year + '-')).map(r => parseInt(r.batch_id.split('-').pop()) || 0);
    return 'PAY-BATCH-' + year + '-' + String((b.length ? Math.max(...b) + 1 : 1)).padStart(4, '0');
  }, []);
  const nextBenCode = useCallback((existing) => {
    const nums = existing.map(b => parseInt((b.code || '').split('-')[1]) || 0);
    return 'BEN-' + String((nums.length ? Math.max(...nums) + 1 : 1)).padStart(3, '0');
  }, []);

  const mapUser = (r) => r ? ({ id: r.id, name: r.name, username: r.username, role: r.role, active: r.active, createdDate: r.created_at }) : null;
  const mapProject = (r) => r ? ({ id: r.id, name: r.name, assignedPM: r.assigned_pm, status: r.status, createdDate: r.created_at, modifiedDate: r.modified_at }) : null;
  const mapPaymentType = (r) => r ? ({ id: r.id, name: r.name, description: r.description, requiredDocs: r.required_docs, active: r.active }) : null;
  const mapBeneficiary = (r) => r ? ({
    id: r.id, code: r.code, name: r.name, type: r.type, address: r.address, phone: r.phone, email: r.email,
    bankName: r.bank_name, bankAddress: r.bank_address, accountNumber: r.account_number, iban: r.iban, swiftCode: r.swift_code,
    nationalId: r.national_id_doc, username: r.username, password: r.password_hash, assignedPM: r.assigned_pm,
    createdBy: r.created_by, active: r.active, createdDate: r.created_at, lastModifiedDate: r.modified_at,
  }) : null;
  const mapRequest = (r) => r ? ({
    id: r.id, requestDate: r.request_date, createdBy: r.created_by, createdByRole: r.created_by_role,
    beneficiaryId: r.beneficiary_id, beneficiaryName: r.beneficiary_name,
    projectId: r.project_id, projectName: r.project_name, pmId: r.pm_id, pmName: r.pm_name,
    paymentTypeId: r.payment_type_id, paymentType: r.payment_type,
    amount: r.amount, currency: r.currency, description: r.description,
    attachedInvoice: r.attached_invoice, attachedDoc: r.attached_doc, invoiceAvailable: r.invoice_available,
    jobDateFrom: r.job_date_from, jobDateTo: r.job_date_to, status: r.status,
    paymentDirect: r.payment_direct, recipientName: r.recipient_name, recipientBank: r.recipient_bank,
    recipientBankAddress: r.recipient_bank_address, recipientAccount: r.recipient_account, recipientIban: r.recipient_iban, recipientSwift: r.recipient_swift,
    accountantApprovalDate: r.accountant_approval_date, accountantComments: r.accountant_comments,
    mdApprovalDate: r.md_approval_date, mdComments: r.md_comments,
    exportStatus: r.export_status, exportDate: r.export_date, batchId: r.batch_id,
    history: r.audit_log ? r.audit_log.sort((a,b)=>new Date(a.action_date)-new Date(b.action_date)).map(h => ({ date: h.action_date, user: h.user_name, role: h.user_role, action: h.action, comments: h.comments })) : [],
  }) : null;

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [u, p, pt, b, r] = await Promise.all([
        supabase.from('users').select('*').order('created_at'),
        supabase.from('projects').select('*').order('created_at'),
        supabase.from('payment_types').select('*').order('name'),
        supabase.from('beneficiaries').select('*').order('created_at'),
        supabase.from('payment_requests').select('*, audit_log(*)').order('created_at', { ascending: false }),
      ]);
      if (u.data) setUsers(u.data.map(mapUser));
      if (p.data) setProjects(p.data.map(mapProject));
      if (pt.data) setPaymentTypes(pt.data.map(mapPaymentType));
      if (b.data) setBeneficiaries(b.data.map(mapBeneficiary));
      if (r.data) setRequests(r.data.map(mapRequest));
    } catch (e) { console.error('loadAll', e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const login = async (username, password) => {
    const { data, error } = await supabase.from('users').select('*').eq('username', username.trim()).eq('active', true).single();
    if (error || !data) return { ok: false, error: 'Invalid username or password' };
    if (data.password_plain !== password) return { ok: false, error: 'Invalid username or password' };
    const user = mapUser(data);
    setCurrentUser(user);
    return { ok: true, user };
  };
  const logout = () => setCurrentUser(null);

  const addRequest = async (data) => {
    const id = nextRequestId(requests);
    const history = [{ request_id: id, user_name: data.createdByRole === 'beneficiary' ? data.beneficiaryName : data.pmName, user_role: data.createdByRole === 'beneficiary' ? 'Beneficiary' : 'Project Manager', action: 'Request created', comments: '' }];
    if (data.status === 'Submitted to Accountant') history.push({ request_id: id, user_name: data.pmName, user_role: 'Project Manager', action: 'Submitted to Accountant', comments: '' });
    if (data.status === 'Pending PM Review') history.push({ request_id: id, user_name: data.beneficiaryName, user_role: 'Beneficiary', action: 'Request submitted by beneficiary', comments: '' });
    const row = {
      id, created_by: data.createdBy, created_by_role: data.createdByRole,
      beneficiary_id: data.beneficiaryId, beneficiary_name: data.beneficiaryName,
      project_id: data.projectId || null, project_name: data.projectName,
      pm_id: data.pmId || null, pm_name: data.pmName,
      payment_type_id: data.paymentTypeId || null, payment_type: data.paymentType,
      amount: data.amount, currency: data.currency, description: data.description,
      attached_invoice: data.attachedInvoice, attached_doc: data.attachedDoc,
      invoice_available: data.invoiceAvailable !== false,
      job_date_from: data.jobDateFrom || null, job_date_to: data.jobDateTo || null, status: data.status,
      payment_direct: data.paymentDirect !== false,
      recipient_name: data.recipientName || null, recipient_bank: data.recipientBank || null,
      recipient_bank_address: data.recipientBankAddress || null, recipient_account: data.recipientAccount || null,
      recipient_iban: data.recipientIban || null, recipient_swift: data.recipientSwift || null,
      accountant_approval_date: null, accountant_comments: '', md_approval_date: null, md_comments: '',
      export_status: false, export_date: null, batch_id: null,
    };
    const { error } = await supabase.from('payment_requests').insert(row);
    if (error) { console.error('addRequest', error); return; }
    await supabase.from('audit_log').insert(history);
    await loadAll();
  };

  const updateRequest = async (id, changes, historyEntry) => {
    const keyMap = {
      status: 'status', amount: 'amount', currency: 'currency', description: 'description',
      projectId: 'project_id', projectName: 'project_name', paymentTypeId: 'payment_type_id', paymentType: 'payment_type',
      attachedInvoice: 'attached_invoice', attachedDoc: 'attached_doc', invoiceAvailable: 'invoice_available', jobDateFrom: 'job_date_from', jobDateTo: 'job_date_to',
      paymentDirect: 'payment_direct', recipientName: 'recipient_name', recipientBank: 'recipient_bank',
      recipientBankAddress: 'recipient_bank_address', recipientAccount: 'recipient_account', recipientIban: 'recipient_iban', recipientSwift: 'recipient_swift',
      accountantApprovalDate: 'accountant_approval_date', accountantComments: 'accountant_comments',
      mdApprovalDate: 'md_approval_date', mdComments: 'md_comments',
      exportStatus: 'export_status', exportDate: 'export_date', batchId: 'batch_id',
    };
    const dbChanges = {};
    Object.entries(changes).forEach(([k, v]) => { if (keyMap[k]) dbChanges[keyMap[k]] = v; });
    const { error } = await supabase.from('payment_requests').update(dbChanges).eq('id', id);
    if (error) { console.error('updateRequest', error); return; }
    if (historyEntry) await supabase.from('audit_log').insert({ request_id: id, user_name: historyEntry.user, user_role: historyEntry.role, action: historyEntry.action, comments: historyEntry.comments || '' });
    await loadAll();
  };

  const addBeneficiary = async (data) => {
    const code = nextBenCode(beneficiaries);
    const row = {
      code, name: data.name, type: data.type, address: data.address, phone: data.phone, email: data.email,
      bank_name: data.bankName, bank_address: data.bankAddress, account_number: data.accountNumber, iban: data.iban, swift_code: data.swiftCode,
      national_id_doc: data.nationalId, username: data.username || null, password_hash: data.password || null,
      assigned_pm: data.assignedPM || null, created_by: data.createdBy, active: true,
    };
    const { data: ins, error } = await supabase.from('beneficiaries').insert(row).select().single();
    if (error) { console.error('addBeneficiary', error); return null; }
    await loadAll();
    return mapBeneficiary(ins);
  };

  const updateBeneficiary = async (id, changes) => {
    const keyMap = { name:'name', type:'type', address:'address', phone:'phone', email:'email', bankName:'bank_name', bankAddress:'bank_address', accountNumber:'account_number', iban:'iban', swiftCode:'swift_code', nationalId:'national_id_doc', username:'username', password:'password_hash', assignedPM:'assigned_pm', active:'active' };
    const dbChanges = {};
    Object.entries(changes).forEach(([k, v]) => { if (keyMap[k]) dbChanges[keyMap[k]] = v; });
    const { error } = await supabase.from('beneficiaries').update(dbChanges).eq('id', id);
    if (error) { console.error('updateBeneficiary', error); return; }
    setBeneficiaries(prev => prev.map(b => b.id === id ? { ...b, ...changes } : b));
    await loadAll();
  };

  const addProject = async (data) => {
    const { error } = await supabase.from('projects').insert({ name: data.name, assigned_pm: data.assignedPM || null, status: data.status || 'active' });
    if (error) { console.error('addProject', error); return; }
    await loadAll();
  };
  const updateProject = async (id, changes) => {
    const dbChanges = {};
    if (changes.name !== undefined) dbChanges.name = changes.name;
    if (changes.assignedPM !== undefined) dbChanges.assigned_pm = changes.assignedPM;
    if (changes.status !== undefined) dbChanges.status = changes.status;
    const { error } = await supabase.from('projects').update(dbChanges).eq('id', id);
    if (error) { console.error('updateProject', error); return; }
    await loadAll();
  };

  const addPaymentType = async (data) => {
    const { error } = await supabase.from('payment_types').insert({ name: data.name, description: data.description, required_docs: data.requiredDocs, active: true });
    if (error) { console.error('addPaymentType', error); return; }
    await loadAll();
  };
  const updatePaymentType = async (id, changes) => {
    const dbChanges = {};
    if (changes.name !== undefined) dbChanges.name = changes.name;
    if (changes.description !== undefined) dbChanges.description = changes.description;
    if (changes.requiredDocs !== undefined) dbChanges.required_docs = changes.requiredDocs;
    if (changes.active !== undefined) dbChanges.active = changes.active;
    const { error } = await supabase.from('payment_types').update(dbChanges).eq('id', id);
    if (error) { console.error('updatePaymentType', error); return; }
    await loadAll();
  };

  const addUser = async (data) => {
    const { error } = await supabase.from('users').insert({ name: data.name, username: data.username, password_plain: data.password, role: data.role, active: true });
    if (error) { console.error('addUser', error); return { ok:false, error: error.message }; }
    await loadAll();
    return { ok: true };
  };

  const exportBatch = async (requestIds) => {
    const batchId = nextBatchId(requests);
    const exportDate = new Date().toISOString();
    const userName = currentUser ? currentUser.name : 'Managing Director';
    for (const id of requestIds) {
      await supabase.from('payment_requests').update({ status: 'Exported for Banking', export_status: true, export_date: exportDate, batch_id: batchId }).eq('id', id);
      await supabase.from('audit_log').insert({ request_id: id, user_name: userName, user_role: 'Managing Director', action: 'Exported for Banking', comments: 'Batch: ' + batchId });
    }
    await loadAll();
    return batchId;
  };

  return (
    <AppContext.Provider value={{
      currentUser, login, logout, loading,
      users, addUser, projects, addProject, updateProject,
      paymentTypes, addPaymentType, updatePaymentType,
      beneficiaries, addBeneficiary, updateBeneficiary,
      requests, addRequest, updateRequest, exportBatch, loadAll,
    }}>{children}</AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
